import { agaveService } from 'app/services/agave.service';
import {
  ERPPostingStatus,
  HighnoteTransactionEvent,
  AgaveAccount,
  CreateAgaveExpenseInput,
  CreateAgaveExpenseLineItemInput,
  CreateAgaveExpensePaymentMethodInput,
} from 'API';
import { PostingContext, Subscription } from './types';
import { createPostingSubscription } from './sharedPostingUtils';
// The context's setTransactionPosting / applyLocalTransactionUpdates are the raw
// action creators (callers wrap them in dispatch). Import them directly so we
// actually dispatch — a bare call is a no-op, which left the posting spinner
// (postingTransactionIds) stuck on the poll-based QBD path. Dispatching the real
// actions also drops the `as any` casts these calls used to need. (SEE-1042)
import {
  setTransactionPosting as setTransactionPostingAction,
  applyLocalTransactionUpdates as applyLocalTransactionUpdatesAction,
} from 'app/pages/store/transactionsSlice';
import { formatAmountForAgavePost, stripPaidolPrefix } from 'app/services/agave/erpUtils';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';

/**
 * Builds QBD Expense payload for queue-based posting.
 * QBD uses single line item with all available fields combined.
 */
export function buildQBDExpensePayload(
  transaction: HighnoteTransactionEvent,
  agaveAccount: AgaveAccount | undefined,
  agaveAccountToken: string
): CreateAgaveExpenseInput {
  const { amount } = formatAmountForAgavePost(transaction.approvedAmount?.value ?? 0, transaction.status);
  const paymentNumber = transaction.id.substring(0, 10);
  const paymentDate = new Date(transaction.createdAt).toISOString().split('T')[0];
  const description = transaction.merchantName + (transaction.note ? ' - ' + transaction.note : '');

  // For QBD: If sub-job is selected, use sub-job as customer_id (more specific level)
  // In QBD, there's only ONE CustomerRef field per line item - it should point to the most specific job level
  // The sub_job_id field is ignored by Agave for QBD, so we use customer_id for the sub-job instead
  const customerId = transaction.agaveSubJob?.source_id
    ? `source_id:${transaction.agaveSubJob.source_id}`
    : transaction.agaveProject?.source_id
    ? `source_id:${transaction.agaveProject.source_id}`
    : undefined;

  // Create ONE line item with ALL available fields
  const lineItem: CreateAgaveExpenseLineItemInput = {
    amount: amount,
    description,
    ...(transaction.agaveGlCodeId && { ledger_account_id: stripPaidolPrefix(transaction.agaveGlCodeId) }),
    ...(customerId && { customer_id: customerId }),
    ...(transaction.agaveCostCode?.source_id && {
      item_id: `source_id:${transaction.agaveCostCode.source_id}`,
    }),
    ...(transaction.agaveCostType?.name && { type: transaction.agaveCostType.name }),
  };

  const expense: CreateAgaveExpenseInput = {
    account_token: agaveAccountToken,
    erp: agaveAccount?.source_system?.slug || AgaveSourceSystem.QUICKBOOKS_DESKTOP,
    description,
    number: paymentNumber,
    payment_date: paymentDate,
    payment_method: CreateAgaveExpensePaymentMethodInput.CreditCardCharge,
    vendor_id: stripPaidolPrefix(transaction.agaveVendorId),
    line_items: [lineItem],
  };

  return expense;
}

export interface QBDPostingParams {
  transactionId: string;
  paidolId: string;
  transaction: HighnoteTransactionEvent;
  agaveAccount?: AgaveAccount;
  agaveAccountToken: string;
}

// QBD subscriptions are intentionally kept open with no auto-timeout (the poll
// can land minutes to hours later) and outlive the component that started them,
// so nothing else naturally closes them if this function is ever invoked twice
// for the same transaction (e.g. a retry path not gated by the UI's posting
// spinner). Track the open subscription per transactionId here so a second call
// reuses it instead of leaking a duplicate. The entry is removed via the
// `onClose` hook, which sharedPostingUtils calls from inside its OWN cleanup()
// on every terminal path — a poll-delivered COMPLETED/FAILED included, not just
// the early-return branches below that call `.unsubscribe()` themselves.
const openQbdSubscriptions = new Map<string, Subscription>();

// Test-only: this module-level map otherwise leaks tracked subscriptions across
// test cases that reuse the same transactionId.
export function __resetQbdSubscriptionRegistryForTests(): void {
  openQbdSubscriptions.clear();
}

function getOrCreateQbdSubscription(transactionId: string, context: PostingContext): Subscription {
  const existing = openQbdSubscriptions.get(transactionId);
  if (existing) return existing;

  const subscription = createPostingSubscription(
    transactionId,
    context,
    {},
    {
      timeoutMs: null,
      onClose: () => openQbdSubscriptions.delete(transactionId),
    }
  );
  openQbdSubscriptions.set(transactionId, subscription);
  return subscription;
}

/**
 * Handles QBD posting via queue
 */
export async function postQBDTransaction(params: QBDPostingParams, context: PostingContext): Promise<void> {
  const { transactionId, paidolId, transaction: txn, agaveAccount, agaveAccountToken } = params;
  const { dispatch, enqueueSnackbar, t, setIsAgaveLoading, captureException } = context;

  setIsAgaveLoading(true);

  try {
    // Build posting data
    const postingData = buildQBDExpensePayload(txn, agaveAccount, agaveAccountToken);
    const postingDataString = JSON.stringify(postingData);

    // Set up subscription BEFORE starting the async process
    // This ensures we catch status updates even if posting completes very quickly.
    // QBD's terminal status only lands whenever the customer's QuickBooks Web
    // Connector next polls the backend — minutes to hours away — so disable the
    // shared 90s auto-timeout that assumes near-synchronous ERPs; only a real
    // terminal COMPLETED/FAILED update (or an early-return branch below) ends
    // this subscription. (SEE-1042)
    const subscription = getOrCreateQbdSubscription(transactionId, context);

    // Start the async QBD posting via the CDK v2-agave Lambda (SEE-308).
    // agaveService throws on transport / GraphQL errors, so we only need to
    // branch on the returned status.
    const result = await agaveService.startQBDPosting({
      input: {
        transactionId,
        paidolId,
        erp: AgaveSourceSystem.QUICKBOOKS_DESKTOP, // Use constant for type safety
        postingType: 'EXPENSE',
        postingData: postingDataString, // Send as JSON string, not object
      },
    });

    // Check if mutation succeeded and status is PENDING
    const responseStatus = result?.status;

    if (responseStatus === ERPPostingStatus.PENDING) {
      // QBD posting is poll-based (SEE-1041): the charge is durably enqueued and
      // reaches a terminal COMPLETED/FAILED only on the customer's next Web
      // Connector poll, which may be minutes/hours away. There is no synchronous
      // completion to await, so don't hold the posting spinner — clear it and
      // reflect the durable "Processing" state now. The per-transaction
      // subscription (opened above, with its auto-timeout disabled) stays open
      // and surfaces the eventual terminal status whenever the poll lands —
      // do NOT unsubscribe here, or nothing is listening when it arrives.
      dispatch(setTransactionPostingAction({ id: transactionId, isPosting: false }));
      setIsAgaveLoading(false);
      dispatch(
        applyLocalTransactionUpdatesAction([
          {
            id: transactionId,
            changes: { erpPostingStatus: ERPPostingStatus.PROCESSING },
          },
        ])
      );
      enqueueSnackbar(t('submitExpense.postingExpense'), { variant: 'info' });
      return;
    } else if (responseStatus === ERPPostingStatus.COMPLETED) {
      // Transaction was already posted — dispatcher rejected the re-post (SEE-726)
      subscription.unsubscribe();
      dispatch(setTransactionPostingAction({ id: transactionId, isPosting: false }));
      setIsAgaveLoading(false);
      enqueueSnackbar(t('submitExpense.alreadyPosted'), { variant: 'info' });
      return;
    } else if (responseStatus === ERPPostingStatus.PROCESSING) {
      // Posting already in progress — dispatcher rejected the duplicate request (SEE-726)
      subscription.unsubscribe();
      dispatch(setTransactionPostingAction({ id: transactionId, isPosting: false }));
      setIsAgaveLoading(false);
      enqueueSnackbar(t('submitExpense.postingInProgress'), { variant: 'info' });
      return;
    } else {
      subscription.unsubscribe();
      // FAILED or an unexpected status — the post did not get enqueued, so reflect
      // the terminal FAILED state. Use the Amplify ERPPostingStatus enum (the type
      // of the HighnoteTransactionEvent field) rather than the CDK ErpPostingStatus
      // that `result.status` carries — they share values but are nominally distinct
      // generated enums, which is what the old `as any` cast was papering over.
      const errorMessage = result?.error || 'Failed to start QBD posting';

      // Update Redux to clear posting state immediately
      dispatch(
        applyLocalTransactionUpdatesAction([
          {
            id: transactionId,
            changes: {
              erpPostingStatus: ERPPostingStatus.FAILED,
              erpPostingError: errorMessage,
            },
          },
        ])
      );

      throw new Error(errorMessage);
    }
  } catch (error) {
    // IMPORTANT: Clear posting state FIRST to prevent UI from showing "posting" status
    // This handles GraphQL validation errors that occur before the mutation reaches the backend
    dispatch(setTransactionPostingAction({ id: transactionId, isPosting: false }));
    setIsAgaveLoading(false);

    captureException(error);
    const errorMessage = (error as Error).message || t('submitExpense.error');
    enqueueSnackbar(errorMessage, { variant: 'error' });

    // Ensure transaction status is updated in Redux
    dispatch(
      applyLocalTransactionUpdatesAction([
        {
          id: transactionId,
          changes: {
            erpPostingStatus: null, // Clear posting status on error
            erpPostingError: errorMessage,
          },
        },
      ])
    );
  }
}
